import * as fx from '../audio/fx';
import { app, commit, on } from '../app/state';
import { MAX_SIDE_TIMERS, MINUTE } from '../core/constants';
import type { SideTimer } from '../core/types';
import { clamp, fmtClock, uid } from '../core/util';
import { notify } from '../platform/notify';
import { announce } from './announcer';
import { $, $$, holdable, ic } from './dom';
import { toast } from './toast';

const sideList = $('#sideList');
const flyout = $('#trayPop');
const trayBtn = $('#trayBtn');
const els = new Map<string, { time: HTMLElement; bar: HTMLElement }>();
let custom = 7;

const side = () => app.data.side;
const save = () => commit(['side']);

function nextName() {
  const used = new Set(side().map((s) => s.name));
  for (let i = 1; i <= 9; i++) if (!used.has(`T${i}`)) return `T${i}`;
  return 'T0';
}

function add(min: number) {
  if (side().length >= MAX_SIDE_TIMERS) return toast('Four side timers is plenty', 'var(--tomato)');
  const total = min * MINUTE;
  side().push({
    id: uid(),
    name: nextName(),
    total,
    remaining: total,
    endAt: Date.now() + total,
    running: true,
    done: false,
  });
  fx.tap();
  save();
  sideList.lastElementChild?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  announce(`${min} minute side timer started`);
}

function remove(id: string) {
  app.data.side = side().filter((t) => t.id !== id);
  save();
}

function toggle(t: SideTimer) {
  if (t.done) return;
  if (t.running) {
    t.remaining = Math.max(0, t.endAt - Date.now());
    t.running = false;
  } else {
    t.endAt = Date.now() + t.remaining;
    t.running = true;
  }
  save();
}

function finish(t: SideTimer) {
  if (t.done) return;
  t.running = false;
  t.remaining = 0;
  t.done = true;
  fx.pips();
  toast(`Side timer ${t.name} finished — time is up`, 'var(--tomato)');
  void notify(
    'Side timer finished',
    `${t.name}: ${Math.round(t.total / MINUTE)} minutes are up.`,
    `pomodoro-side-${t.id}`,
  );
  save();
  setTimeout(() => remove(t.id), 4200);
}

function render() {
  // Keyboard focus on a timer's button survives the rebuild (or moves to a neighbour if removed).
  const a = document.activeElement;
  const focusedRow =
    a instanceof HTMLElement && sideList.contains(a) ? a.closest<HTMLElement>('.side') : null;
  const focus = focusedRow && {
    id: focusedRow.dataset.id,
    index: [...sideList.children].indexOf(focusedRow),
    btn: [...focusedRow.querySelectorAll('button')].indexOf(a as HTMLButtonElement),
  };
  sideList.innerHTML = '';
  els.clear();
  for (const t of side()) {
    const d = document.createElement('div');
    d.className = `side${t.done ? ' done' : ''}`;
    d.dataset.id = t.id;
    d.setAttribute('role', 'listitem');
    d.innerHTML = `<span class="s-name"></span><b class="s-time"></b>
      <span class="s-bar" aria-hidden="true"><i></i></span>
      <button class="s-ctlB" type="button">${ic(t.running ? 'pause' : 'play')}</button>
      <button class="s-ctlB" type="button">${ic('x')}</button>`;
    d.querySelector('.s-name')!.textContent = t.name;
    const time = d.querySelector<HTMLElement>('.s-time')!;
    const bar = d.querySelector<HTMLElement>('.s-bar i')!;
    time.textContent = fmtClock(t.remaining);
    bar.style.width = `${((t.remaining / t.total) * 100).toFixed(1)}%`;
    const [pp, rm] = d.querySelectorAll<HTMLButtonElement>('.s-ctlB');
    pp!.setAttribute('aria-label', `${t.running ? 'Pause' : 'Resume'} side timer ${t.name}`);
    rm!.setAttribute('aria-label', `Remove side timer ${t.name}`);
    pp!.addEventListener('click', () => toggle(t));
    rm!.addEventListener('click', () => remove(t.id));
    els.set(t.id, { time, bar });
    sideList.appendChild(d);
  }
  if (focus) {
    const rows = [...sideList.children] as HTMLElement[];
    const row = rows.find((r) => r.dataset.id === focus.id) ?? rows[Math.min(focus.index, rows.length - 1)];
    (row?.querySelectorAll('button')[Math.max(0, focus.btn)] ?? trayBtn).focus({ preventScroll: true });
  }
  syncOverflow();
}

/** Fades whichever edge has timers scrolled out of view. */
function syncOverflow() {
  const { scrollLeft, clientWidth, scrollWidth } = sideList;
  sideList.classList.toggle('more-start', scrollLeft > 2);
  sideList.classList.toggle('more-end', scrollLeft + clientWidth < scrollWidth - 2);
}

/** Called each frame and by the heartbeat. */
export function tickSide() {
  const now = Date.now();
  for (const t of side()) {
    if (!t.running) continue;
    t.endAt = Math.min(t.endAt, now + t.total); // the system clock moved backwards
    t.remaining = Math.max(0, t.endAt - now);
    if (t.remaining <= 0) finish(t);
    else {
      const e = els.get(t.id);
      if (e) {
        e.time.textContent = fmtClock(t.remaining);
        e.bar.style.width = `${((t.remaining / t.total) * 100).toFixed(1)}%`;
      }
    }
  }
}

function setFlyout(open: boolean) {
  flyout.hidden = !open;
  trayBtn.setAttribute('aria-expanded', String(open));
  if (open) flyout.querySelector<HTMLElement>('button')?.focus();
}
export const closeFlyout = () => {
  if (flyout.hidden) return false;
  setFlyout(false);
  trayBtn.focus();
  return true;
};

export function initSideTimers() {
  trayBtn.addEventListener('click', () => setFlyout(flyout.hidden !== false));
  document.addEventListener('pointerdown', (e) => {
    if (!flyout.hidden && !$('#tray').contains(e.target as Node)) setFlyout(false);
  });
  for (const b of $$('#trayPop .fo-chips button'))
    b.addEventListener('click', () => {
      add(Number(b.dataset.m));
      setFlyout(false);
    });
  const step = (d: number) => () => {
    const v = clamp(custom + d, 1, 120);
    if (v !== custom) {
      custom = v;
      $('#cVal').textContent = String(custom);
      fx.tick();
    }
  };
  holdable($('#cMinus'), step(-1));
  holdable($('#cPlus'), step(1));
  $('#cStart').addEventListener('click', () => {
    add(custom);
    setFlyout(false);
  });
  // drop timers that finished while the app was closed
  for (const t of side()) if (t.done) setTimeout(() => remove(t.id), 4200);
  sideList.addEventListener('scroll', syncOverflow, { passive: true });
  window.addEventListener('resize', syncOverflow);
  on('side', render);
  render();
}
